import { ColumnDef } from "@/components/common/DataTable";
import ApplicantContextMenu from "@/components/staffing/contextMenus/ApplicantContextMenu";
import ClientStaffingContextMenu from "@/components/staffing/contextMenus/ClientStaffingContextMenu";
import StaffContextMenu from "@/components/staffing/contextMenus/StaffContextMenu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { StaffingApplicant } from "@/types/common/applicant";
import { StaffingClient } from "@/types/common/client";
import { StaffingStaffMember } from "@/types/common/staffMember";
import { Posting } from "@/types/staffing/posting";
import { Process, ProcessRow } from "@/types/staffing/process";
import { getApplicantStatusBadgeColor, getClientStatusBadgeColor, getNumProcessesColor, getStaffEmpStatusBadgeColor, getStatusBadgeColor } from "@/utils/staffingUtil";
import { Skeleton } from "../../ui/skeleton";
import PercentBar from "../../common/PercentBar";
import NotesColumn from "./NotesColumn";
import { formatDateOnly, isSingleDayTimeEntryThisMonth, TIME_ENTRY_HIGHLIGHT_CLASS } from "@/utils/timeEntryDates";
export const getPostingsColumns = (
    onProcessClick: (processes: Process[], clientId: number, clientName: string | null, postingId: number) => void
): ColumnDef<Posting>[] => [
        {
            key: 'Id',
            header: 'ID',
            sortable: true,
            sticky: true
        },
        {
            key: 'Client.NameFl',
            header: 'Client',
            sortable: true,
            serverSortKey: 'Client/NameFL',
            sticky: true,
            render: (row) => <ClientStaffingContextMenu clientId={row.Client.Id} clientName={row.Client.NameFL} isPosting={true} isMatch={false} isWidget={false} serviceType={row.ServiceType} postingId={row.Id} />
        },
        {
            key: 'Status',
            header: 'Posting Status',
            sortable: true,
            render: (row) => <Badge className={getStatusBadgeColor(row.Status)}>{row.Status}</Badge>
        },
        {
            key: 'Client.ClientStatus',
            header: 'Client Status',
            sortable: false,
            render: (row) => <Badge className={cn(getClientStatusBadgeColor(row.Client.ClientStatus), 'ml-2')}>{row.Client.ClientStatus}</Badge>
        },
        {
            key: 'ClientId',
            header: 'Client ID',
            sortable: false
        },
        {
            key: 'ServiceType',
            header: 'Service Type',
            sortable: true
        },
        {
            key: 'ServiceRequestedBy',
            header: 'Source',
            sortable: false,
            render: (row) => {
                if (row.ServiceRequestedBy !== null) {
                    return 'Service Request'
                }
                else if (row.Reasons == null) {
                    return 'Manually Added on Staffing Dashboard'
                }
                else {
                    return 'Daily Service'
                }
            }
        },
        {
            key: 'RequestedBy',
            header: 'Requested By',
            sortable: false,
            render: (row) => row.ServiceRequestedBy
                ? row.ServiceRequestedBy.replace(/^positive\\/i, '')
                : <span className="text-muted-foreground">—</span>
        },
        {
            key: 'StaffedProvider',
            header: 'Staffed Provider',
            sortable: false,
            render: (row) => {
                const provider = row.Processes.find(x => x.Status === 'Staffed');
                return provider ? provider.Staff : 'Not Staffed';
            }
        },
        {
            key: 'ProcessCount',
            header: 'Staffing in Process',
            sortable: true,
            render: (row) => {
                return (
                    <Button
                        size="sm"
                        variant="outline"
                        className={getNumProcessesColor(row.ProcessCount)}
                        disabled={!row.ProcessCount}
                        onClick={() => onProcessClick(row.Processes, row.ClientId, row.Client.NameFL, row.Id)}
                    >
                        {row.ProcessCount}
                    </Button>
                );
            }
        },
        {
            key: 'Reasons',
            header: 'Staffing Reasons',
            sortable: false,
            render: (row) => {
                if (!row.Reasons || row.Reasons.length === 0) return <span className="text-muted-foreground">—</span>;
                const sorted = [...row.Reasons].sort((a, b) => a.Priority - b.Priority);
                return (
                    <ul className="list-disc list-inside space-y-0.5 text-sm">
                        {sorted.map(r => <li key={r.ReasonId}>{r.Reason}</li>)}
                    </ul>
                );
            }
        },
        {
            key: 'FundingSource',
            header: 'Funding Source(s)',
            sortable: true,
            render: (row) => row.FundingSource ? row.FundingSource.replaceAll('[', '').replaceAll(']', '').replaceAll(',', ', ') : ''
        },
        {
            key: 'BaRbtAllowed',
            header: 'BA/RBT Allowed',
            sortable: true
        },
        {
            key: 'Client.EnrollmentStatus',
            header: 'Enrollment Status',
            sortable: true,
            serverSortKey: 'Client/EnrollmentStatus',
        },
        {
            key: 'Client.Age',
            header: 'Age',
            sortable: true,
            serverSortKey: 'Client/Age',
        },
        {
            key: 'Client.ParentCaregiver1City',
            header: 'Client City',
            sortable: true,
            serverSortKey: 'Client/ParentCaregiver1City',
        },
        {
            key: 'Telehealth',
            header: 'Telehealth',
            sortable: true,
            render: (row) => row.Telehealth ? 'Yes' : 'No'
        },
        {
            key: 'Created',
            header: 'Created',
            sortable: true,
            render: (row) => new Date(row.Created).toLocaleDateString()
        },
        {
            key: 'DaysWaiting',
            header: 'Days Waiting',
            sortable: true,
            serverSortKey: 'Created',
            serverSortInvert: true,
            sortValue: (row) => Math.floor((Date.now() - new Date(row.Created).getTime()) / 86400000),
            render: (row) => Math.floor((Date.now() - new Date(row.Created).getTime()) / 86400000)
        }
    ];

export const staffColumns: ColumnDef<StaffingStaffMember>[] = [
    {
        key: 'FullName',
        header: 'Name',
        sortable: true,
        sticky: true,
        render: (row) => <StaffContextMenu staff={row.LoginName} staffName={row.FullName} staffRole={row.Role} isPosting={false} isMatch={false} />
    },
    {
        key: 'Role',
        header: 'Role',
        sortable: true
    },
    {
        key: 'NoteCount',
        header: 'Notes',
        sortable: true,
        render: (row) => <NotesColumn Count={row.NoteCount} UserType={'staff'} Identifier={`positive\\${row.LoginName}`} DisplayName={row.FullName} />
    },
    {
        key: 'CourseCAProgress',
        header: 'CA Progress',
        sortable: true,
        render: (row) => row.CourseCAProgress == null
            ? <span className="text-muted-foreground">—</span>
            : <PercentBar percent={row.CourseCAProgress} />
    },
    {
        key: 'CourseRBTProgress',
        header: '40hr Progress',
        sortable: true,
        render: (row) => row.CourseRBTProgress == null
            ? <span className="text-muted-foreground">—</span>
            : <PercentBar percent={row.CourseRBTProgress} />
    },
    {
        key: 'City',
        header: 'City',
        sortable: true
    },
    {
        key: 'Zip',
        header: 'Zip',
        sortable: true
    },
    {
        key: 'EmpStatus',
        header: 'Status',
        sortable: true,
        render: (row) => <Badge className={getStaffEmpStatusBadgeColor(row.EmpStatus)}>{row.EmpStatus}</Badge>
    },
    {
        key: 'AnalystLevel',
        header: 'Analyst Level',
        sortable: true
    },
    {
        key: 'HoursRequiredperWeek',
        header: 'Hours Required',
        sortable: true
    },
    {
        key: 'FirstTimeEntryDate',
        header: 'First Time Entry',
        sortable: true,
        cellClassName: (row) => isSingleDayTimeEntryThisMonth(row.FirstTimeEntryDate, row.LastTimeEntryDate) ? TIME_ENTRY_HIGHLIGHT_CLASS : undefined,
        render: (row) => formatDateOnly(row.FirstTimeEntryDate)
    },
    {
        key: 'LastTimeEntryDate',
        header: 'Last Time Entry',
        sortable: true,
        cellClassName: (row) => isSingleDayTimeEntryThisMonth(row.FirstTimeEntryDate, row.LastTimeEntryDate) ? TIME_ENTRY_HIGHLIGHT_CLASS : undefined,
        render: (row) => formatDateOnly(row.LastTimeEntryDate)
    },
];

export const clientsColumns: ColumnDef<StaffingClient>[] = [
    {
        key: 'Id',
        header: 'Id',
        sortable: false,
        sticky: true
    },
    {
        key: 'NameFL',
        header: 'Name',
        sortable: true,
        sticky: true,
        render: (row) => <ClientStaffingContextMenu clientId={row.Id} clientName={row.NameFL} isPosting={false} isMatch={false} isWidget={false}/>
    },
    {
        key: 'ClientStatus',
        header: 'Status',
        sortable: true,
        render: (row) => <Badge className={cn(getClientStatusBadgeColor(row.ClientStatus), 'ml-2')}>{row.ClientStatus}</Badge>
    },
    {
        key: 'Telehealth',
        header: 'Telehealth',
        sortable: true,
        render: (row) => row.Telehealth ? 'Yes' : 'No'
    },
    {
        key: 'InsuranceCompany',
        header: 'Funding Source(s)',
        sortable: true,
        render: (row) => row.InsuranceCompany?.replaceAll(',', ', ')
    },
    {
        key: 'NoteCount',
        header: 'Notes',
        sortable: true,
        render: (row) => <NotesColumn Count={row.NoteCount} UserType={'client'} Identifier={row.Id.toString()} DisplayName={row.NameFL ?? String(row.Id)} />
    },
    {
        key: 'TotalHoursUsedPercent',
        header: 'Utilization',
        sortable: true,
        render: (row) => !(row.TotalHoursUsedPercent == null) ? row.TotalHoursUsedPercent + '%' : null
    },
    {
        key: 'HoursPossibleToFill',
        header: 'Auth Details',
        sortable: true,
        render: (row) => !(row.HoursPossibleToFill == null) ? row.HoursPossibleToFill + ' hr' : null
    },
    {
        key: 'Age',
        header: 'Age',
        sortable: true
    },
    {
        key: 'ParentCaregiver1City',
        header: 'City',
        sortable: true
    },
    {
        key: 'ParentCaregiver1County',
        header: 'County',
        sortable: true
    },
    {
        key: 'EnrollmentStatus',
        header: 'Enrollment Status',
        sortable: true
    },
    {
        key: 'FirstDOSBillable',
        header: 'First DOS Billed',
        sortable: true,
        render: (row) => row.FirstDOSBillable ? new Date(row.FirstDOSBillable).toLocaleDateString() : null
    },
    {
        key: 'FirstDOSAssistantOrRBT',
        header: 'First DOS Assistant/RBT',
        sortable: true,
        render: (row) => row.FirstDOSAssistantOrRBT ? new Date(row.FirstDOSAssistantOrRBT).toLocaleDateString() : null
    },
    {
        key: 'LastDOSAssistantOrRBT',
        header: 'Last DOS Assistant/RBT',
        sortable: true,
        render: (row) => row.LastDOSAssistantOrRBT ? new Date(row.LastDOSAssistantOrRBT).toLocaleDateString() : null
    }
];

export const applicantColumns: ColumnDef<StaffingApplicant>[] = [
    {
        key: 'Id',
        header: 'Id',
        sticky: true
    },
    {
        key: 'FullName',
        header: 'Name',
        sortable: true,
        sticky: true,
        render: (row) => <ApplicantContextMenu applicant={row.Email} applicantName={row.FullName} applicantRole={row.Role} isPosting={false} isMatch={false} />
    },
    {
        key: 'Role',
        header: 'Role',
        sortable: true
    },
    {
        key: 'NoteCount',
        header: 'Notes',
        sortable: true,
        render: (row) => <NotesColumn Count={row.NoteCount} UserType={'applicant'} Identifier={row.Email} DisplayName={row.FullName} />
    },
    {
        key: 'City',
        header: 'City',
        sortable: true
    },
    {
        key: 'Zip',
        header: 'Zip',
        sortable: true
    },
    {
        key: 'Status',
        header: 'Status',
        sortable: true,
        render: (row) => <Badge className={`${getApplicantStatusBadgeColor(row.Status)} whitespace-nowrap`}>{row.Status}</Badge>
    },
    {
        key: 'AnalystLevel',
        header: 'Analyst Level',
        sortable: true
    },
    {
        key: 'CourseCAProgress',
        header: 'CA Progress',
        sortable: true,
        render: (row) => row.CourseCAProgress == null
            ? <span className="text-muted-foreground">—</span>
            : <PercentBar percent={row.CourseCAProgress} />
    },
    {
        key: 'CourseRBTProgress',
        header: '40hr Progress',
        sortable: true,
        render: (row) => row.CourseRBTProgress == null
            ? <span className="text-muted-foreground">—</span>
            : <PercentBar percent={row.CourseRBTProgress} />
    },
    {
        key: 'AppliedOn',
        header: 'Applied On / Reapplied On',
        render: (row) => {
            if (row.ReappliedOn) return `${new Date(row.ReappliedOn).toLocaleDateString()} (Reapplied)`;
            if (row.AppliedOn) return new Date(row.AppliedOn).toLocaleDateString();
            return '—';
        }
    },
    {
        key: 'InterviewCompletedOn',
        header: 'Interview Completed',
        render: (row) => row.InterviewCompletedOn ? new Date(row.InterviewCompletedOn).toLocaleDateString() : '—'
    },
    {
        key: 'OfferSentDate',
        header: 'Offer Sent',
        render: (row) => row.OfferSentDate ? new Date(row.OfferSentDate).toLocaleDateString() : '—'
    },
    {
        key: 'LastPortalLogin',
        header: 'Last Portal Login',
        sortable: true,
        render: (row) => row.LastPortalLogin ? new Date(row.LastPortalLogin).toLocaleDateString() : '-'
    },
    {
        key: 'CreatedOn',
        header: 'Created On',
        sortable: true,
        render: (row) => new Date(row.CreatedOn).toLocaleDateString()
    }
];

export const getProcessModalColumns = (clientId: number): ColumnDef<ProcessRow>[] => [
    {
        key: 'Staff',
        header: 'Staff',
        sortable: true,
        render: (row) => {
            return (row.FullName && row.StaffId ? (
                row.Applicant
                    ? <ApplicantContextMenu applicant={row.Staff} applicantName={row.FullName} applicantRole={row.Role} isPosting={true} isMatch={false} />
                    : <StaffContextMenu staff={row.Staff} staffName={row.FullName} staffRole={row.Role} isPosting={true} isMatch={false} clientId={clientId} processId={row.Id} />
            ) : (<Skeleton className="h-4 w-full" />))
        }
    },
    {
        key: 'Status',
        header: 'Status',
        sortable: true
    },
    {
        key: 'Role',
        header: 'Role',
        sortable: true,
        render: (row) => {
            return (
                row.Role
                    ? row.Role
                    : <Skeleton className="h-4 w-full" />
            )
        }
    },
    {
        key: 'Source',
        header: 'Source',
        sortable: true
    },
    {
        key: 'Distance',
        header: 'Distance (mi)',
        sortable: true
    },
    {
        key: 'City',
        header: 'City',
        sortable: true,
        render: (row) => {
            return (
                row.City
                    ? row.City
                    : <Skeleton className="h-4 w-full" />
            )
        }
    },
    {
        key: 'Applicant',
        header: 'Applicant',
        sortable: true,
        render: (row) => row.Applicant ? 'Yes' : 'No'
    }
]